import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Printer, ArrowRight } from 'lucide-react';
import { bookingsAPI } from '../../services/api';
import { useTenantBranding } from '../../context/TenantBrandingContext';
import Barcode from 'react-barcode';

const WorkRequest = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const printRef = useRef();
  const { tenant } = useTenantBranding();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [samplesByDepartment, setSamplesByDepartment] = useState([]);

  useEffect(() => {
    let isCancelled = false;

    async function load() {
      if (!id || id === 'undefined' || id === 'null') {
        if (!isCancelled) navigate('/bookings');
        return;
      }
      try {
        const response = await bookingsAPI.getById(id);
        if (isCancelled) return;
        
        const bookingData = response.data?.data;
        if (bookingData?.payment_status !== 'paid') {
          navigate(`/bookings/${id}`);
          return;
        }
        setBooking(bookingData);
        
        // Group tests by department for samples
        if (bookingData?.tests) {
          const grouped = {};
          bookingData.tests.forEach(test => {
            const department = test.lab_test_id?.department || 'Other';
            if (!grouped[department]) {
              grouped[department] = {
                department,
                specimenType: test.lab_test_id?.specimen_type || '-',
                tubeType: test.lab_test_id?.tube_type || '-',
                tests: []
              };
            }
            grouped[department].tests.push(test.lab_test_id?.name_en || '-');
          });
          
          // Generate sample IDs
          const samples = Object.entries(grouped).map(([dept, data], idx) => ({
            ...data,
            sampleId: `${bookingData.barcode_no}${String(idx + 1).padStart(2, '0')}`
          }));
          
          setSamplesByDepartment(samples);
        }
      } catch (err) {
        if (isCancelled) return;
        console.error('Error fetching booking:', err);
      }
      if (!isCancelled) setLoading(false);
    }

    load();
    return () => { isCancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const getCustomerName = (customer) => {
    if (!customer) return '-';
    if (customer.full_name) return customer.full_name;
    const firstName = customer.first_name || '';
    const lastName = customer.last_name || '';
    return `${firstName} ${lastName}`.trim() || '-';
  };

  const calculateAge = (dob) => {
    if (!dob) return '-';
    const today = new Date();
    const birth = new Date(dob);
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
    return age;
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="text-center py-20">
        <p className="text-gray-500">لم يتم العثور على الحجز</p>
      </div>
    );
  }

  const customer = booking.customer_id;
  
  // Get packages from tests
  const packages = booking.tests?.filter(t => t.package_id)?.map(t => t.package_id?.name_ar || t.package_id?.name_en) || [];
  const uniquePackages = [...new Set(packages)];

  // Get question answers if any
  const questionAnswers = [];
  booking.tests?.forEach(test => {
    if (test.question_answers && test.question_answers.length > 0) {
      test.question_answers.forEach(qa => {
        questionAnswers.push({
          question: qa.question_text,
          answer: qa.answer === true ? 'Yes' : qa.answer === false ? 'No' : qa.answer || '-'
        });
      });
    }
  });

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Print Controls - Hidden when printing */}
      <div className="print:hidden bg-white shadow-md p-4 flex items-center justify-between sticky top-0 z-10">
        <button
          onClick={() => navigate(`/bookings/${id}`)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-800"
        >
          <ArrowRight className="w-5 h-5" />
          العودة للحجز
        </button>
        <h1 className="text-xl font-bold text-gray-800">طلب العمل #{booking.booking_no}</h1>
        <button
          onClick={handlePrint}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg font-medium flex items-center gap-2 hover:bg-blue-700"
        >
          <Printer className="w-5 h-5" />
          طباعة
        </button>
      </div>

      {/* Printable Content */}
      <div ref={printRef} className="max-w-4xl mx-auto bg-white shadow-lg my-8 print:my-0 print:shadow-none">
        {/* Header */}
        <div className="p-6 border-b-2 border-gray-200">
          <div className="flex items-start justify-between">
            {/* Logo */}
            <div className="flex-shrink-0">
              <img src={tenant?.branding?.logoUrl || '/listech-logo.svg'} alt="Lab Logo" className="h-20 object-contain" />
            </div>
            
            {/* Title */}
            <div className="text-center flex-1 px-8">
              <h1 className="text-2xl font-bold text-gray-800">Work Request #{booking.booking_no}</h1>
            </div>
            
            {/* Empty space for balance */}
            <div className="w-20"></div>
          </div>
        </div>

        {/* Patient Information */}
        <div className="p-6">
          <table className="w-full border border-gray-300">
            <tbody>
              <tr>
                <td className="border border-gray-300 px-4 py-2 font-bold bg-gray-50 w-32">Patient</td>
                <td className="border border-gray-300 px-4 py-2 w-64">{getCustomerName(customer)}</td>
                <td className="border border-gray-300 px-4 py-2 font-bold bg-gray-50 w-32">ID Number</td>
                <td className="border border-gray-300 px-4 py-2">{customer?.national_id || '-'}</td>
              </tr>
              <tr>
                <td className="border border-gray-300 px-4 py-2 font-bold bg-gray-50">DOB & Age</td>
                <td className="border border-gray-300 px-4 py-2">
                  {customer?.date_of_birth ? new Date(customer.date_of_birth).toLocaleDateString('en-GB').replace(/\//g, '-') : '-'} ({calculateAge(customer?.date_of_birth)}y)
                </td>
                <td className="border border-gray-300 px-4 py-2 font-bold bg-gray-50">Gender</td>
                <td className="border border-gray-300 px-4 py-2">{customer?.gender?.toLowerCase() || '-'}</td>
              </tr>
              <tr>
                <td className="border border-gray-300 px-4 py-2 font-bold bg-gray-50">Mobile</td>
                <td className="border border-gray-300 px-4 py-2">{customer?.phone || '-'}</td>
                <td className="border border-gray-300 px-4 py-2 font-bold bg-gray-50">Lab Info</td>
                <td className="border border-gray-300 px-4 py-2">{booking?.branch_id?.branch_name || '-'}</td>
              </tr>
              <tr>
                <td className="border border-gray-300 px-4 py-2 font-bold bg-gray-50">Booking Date</td>
                <td className="border border-gray-300 px-4 py-2" colSpan={3}>
                  {new Date(booking.booking_date).toLocaleDateString('en-GB').replace(/\//g, '/')} {new Date(booking.booking_date).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                </td>
              </tr>
              <tr>
                <td className="border border-gray-300 px-4 py-2 font-bold bg-gray-50">Notes</td>
                <td className="border border-gray-300 px-4 py-2" colSpan={3}>
                  {booking.customer_notes || booking.internal_notes || '-'}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Tests Type */}
        <div className="px-6 pb-4">
          <div className="border border-gray-300 p-3 bg-gray-50">
            <span className="font-bold">Tests Type: </span>
            {uniquePackages.length > 0 ? (
              <span>Package: {uniquePackages.join(', ')}</span>
            ) : (
              <span>Individual Tests</span>
            )}
          </div>
        </div>

        {/* Patient Questionnaire Responses */}
        {questionAnswers.length > 0 && (
          <div className="px-6 pb-4">
            <table className="w-full border border-gray-300">
              <thead>
                <tr>
                  <th className="border border-gray-300 px-4 py-2 bg-gray-50 text-right font-bold" colSpan={2}>
                    Patient Questionnaire Responses
                  </th>
                </tr>
              </thead>
              <tbody>
                {questionAnswers.map((qa, idx) => (
                  <tr key={idx}>
                    <td className="border border-gray-300 px-4 py-2 w-2/3">{qa.question}</td>
                    <td className="border border-gray-300 px-4 py-2 w-1/3">{qa.answer}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Samples Table */}
        <div className="px-6 pb-6">
          <table className="w-full border border-gray-300">
            <thead>
              <tr className="bg-gray-100">
                <th className="border border-gray-300 px-4 py-3 text-left font-bold">Specimen Type</th>
                <th className="border border-gray-300 px-4 py-3 text-left font-bold">Sample ID</th>
                <th className="border border-gray-300 px-4 py-3 text-center font-bold">Barcode</th>
                <th className="border border-gray-300 px-4 py-3 text-left font-bold">Tests</th>
              </tr>
            </thead>
            <tbody>
              {samplesByDepartment.map((sample, idx) => (
                <tr key={idx} className="align-top">
                  <td className="border border-gray-300 px-4 py-3">
                    <div className="font-bold">{sample.department}</div>
                    <div className="text-sm text-gray-600">{sample.specimenType}</div>
                    <div className="text-sm text-gray-600">{sample.tubeType}</div>
                  </td>
                  <td className="border border-gray-300 px-4 py-3 font-mono">
                    {sample.sampleId}
                  </td>
                  <td className="border border-gray-300 px-4 py-3 text-center">
                    <div className="flex justify-center">
                      <Barcode 
                        value={sample.sampleId} 
                        width={1.5}
                        height={50}
                        fontSize={12}
                        displayValue={false}
                      />
                    </div>
                  </td>
                  <td className="border border-gray-300 px-4 py-3">
                    <ol className="list-decimal list-inside">
                      {sample.tests.map((test, testIdx) => (
                        <li key={testIdx}>{test}</li>
                      ))}
                    </ol>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="px-6 pb-6 text-center text-sm text-gray-500">
          <p>تم إنشاء هذا التقرير بتاريخ {new Date().toLocaleDateString('en-GB')} الساعة {new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</p>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          body {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          @page {
            size: A4;
            margin: 10mm;
          }
          .print\\:hidden {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
};

export default WorkRequest;

